import { makeStyles, createStyles } from '@material-ui/core/styles';
import { motionDurationsMs } from 'core/components/theme/motion';
import { FLOATING_BUTTON_RIGHT, FLOATING_BUTTON_SIZE } from 'core/hooks/use-floating-position';

const CONTENT_WIDTH = 1200;

const brandStyles = {
  forreston: {
    logo: {
      top: '18%',
      right: 0,
      height: '68vh'
    }
  },
  cfm: {
    logo: {
      left: 0,
      height: '100vh'
    }
  }
};

const styles = (theme) =>
  createStyles({
    '@keyframes gutterBreeze': {
      '0%, 100%': { transform: 'rotate(0deg)' },
      '30%': { transform: 'rotate(1.4deg)' },
      '65%': { transform: 'rotate(-0.7deg)' }
    },
    // Flex-grows to fill the space left in the #root column below the header,
    // rather than a fixed calc(100vh - headerHeight). This keeps the scroll
    // container's bottom on-screen when a body-level banner (Appcues) is shown. (#30714)
    // Condensed layout: the nav rail and the scrolling page sit side by side on the page color.
    condensedShell: {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0
    },
    layout: {
      position: 'relative',
      isolation: 'isolate',
      minWidth: ({ condensed }) => (condensed ? 0 : undefined),
      width: '100%',
      left: 0,
      transition: '225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      flex: '1 1 auto',
      minHeight: 0,
      'overflow-y': 'auto'
    },
    gutterArtwork: {
      '--motion-duration-illustration': `${motionDurationsMs.illustration}ms`,
      display: 'none',
      pointerEvents: 'none',
      // Keep the scenery behind all page content, including expanded panels.
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: ({ footerHeight }) => footerHeight,
      zIndex: -1,
      overflow: 'hidden',
      '@media (min-width: 1800px) and (min-height: 700px)': { display: 'block' },
      '@media print': { display: 'none' },
      '& > svg': {
        position: 'absolute',
        bottom: 0,
        height: 'auto',
        maxHeight: `calc(100% - ${theme.sizes.headerHeight + theme.spacing(12)}px)`,
        userSelect: 'none'
      },
      '& .gutter-tree, & .gutter-leaves, & .gutter-flowers': {
        transformBox: 'fill-box',
        transformOrigin: '50% 100%'
      },
      // A single breeze settles; reading and keyboard work stay still.
      ':root[data-motion="on"] &': {
        '& .gutter-tree, & .gutter-leaves, & .gutter-flowers': {
          animation: '$gutterBreeze var(--motion-duration-illustration) ease-in-out 1',
          animationPlayState: 'paused'
        },
        '& .gutter-leaves': { animationDirection: 'reverse' }
      },
      ':root[data-motion="on"][data-input="pointer"] &': {
        '& .gutter-tree, & .gutter-leaves, & .gutter-flowers': { animationPlayState: 'running' }
      },
      '@media (prefers-reduced-motion: reduce)': {
        ':root[data-motion="on"] &': {
          '& .gutter-tree, & .gutter-leaves, & .gutter-flowers': { animation: 'none' }
        }
      }
    },
    leftArtwork: {
      left: 0,
      width: `min(420px, calc((100% - ${CONTENT_WIDTH}px) / 2 - ${theme.spacing(6)}px))`
    },
    rightArtwork: {
      // Reserve the outer rail for Help and the tour launcher.
      right: theme.spacing(28),
      width: `min(300px, calc((100% - ${CONTENT_WIDTH}px) / 2 - ${theme.spacing(34)}px))`
    },
    loading: {
      display: 'flex',
      paddingTop: 0,
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: ({ showHeader, isCdComparison, condensed }) => ({
      width: CONTENT_WIDTH,
      maxWidth: isCdComparison ? `calc(100% - ${theme.spacing(8)}px)` : undefined,
      height: showHeader ? 'auto' : '100vh',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingTop: showHeader ? theme.spacing(10) : 0,
      // Condensed layout: the page column fills the width beside the nav; condensedContainer adds the padding.
      ...(condensed && { width: '100%', maxWidth: 'none', paddingTop: theme.spacing(8) })
    }),
    condensedContainer: {
      paddingLeft: theme.spacing(6),
      // Clears the help and speed-dial buttons at their default spot, so nothing interactive sits under them.
      paddingRight: FLOATING_BUTTON_RIGHT + FLOATING_BUTTON_SIZE + theme.spacing(4),
      paddingBottom: theme.spacing(14),
      [`@media (max-width: ${theme.screens.tablet - 1}px)`]: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4)
      }
    },
    logo: ({ brand }) => ({
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100vh',
      ...(brandStyles[brand]?.logo || {})
    })
  });

export default makeStyles(styles);
